import ExcelJS from "exceljs";
import { fetchTemplateData } from "../../../DataManagment/Quantative/component/ExcelUploader/template/fetchTemplateData";
import { buildSortedModules } from "../../../DataManagment/Quantative/component/ExcelUploader/template/buildSortedModules";
import { buildTriggerWorkbook } from "./triggerTemplate/buildTriggerWorkbook";
import { buildPeriodSlots } from "../hooks/useTriggers";
import { getStartingMonth } from "../../../../utils/PeriodCalculationUtils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FY_LABEL_RE = /^Financial Year:\s*(.+)$/i;

const safeFile = (s) => String(s).replace(/[^a-z0-9-_]+/gi, "_");

const cellText = (cell) => {
  const v = cell?.value;
  if (v == null) return "";
  if (typeof v === "object") {
    if (typeof v.text === "string") return v.text;
    if (Array.isArray(v.richText)) return v.richText.map((r) => r.text).join("");
    if (typeof v.result !== "undefined") return String(v.result);
    return "";
  }
  return String(v);
};

const cellNumber = (cell) => {
  const v = cell?.value;
  if (v == null || v === "") return "";
  if (typeof v === "number") return String(v);
  if (typeof v === "object" && typeof v.result === "number") return String(v.result);
  return String(v).trim();
};

const triggerDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  try {
    const a = Object.assign(document.createElement("a"), { href: url, download: fileName });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
};

// ─── Generate ─────────────────────────────────────────────────────────────────

/**
 * Builds + downloads a multi-sheet trigger template.
 * Reuses Quantative's data fetch and module grouping; strips assets + evidences;
 * one visible sheet per selected location with FY in row 1 and date-range headers.
 */
export async function generateTemplate(context) {
  const {
    cfg_selectedPeriods,
    cfg_sourceIds,
    cfg_questionIds,
    selectedFinancialYear,
    financialYearLabel,
    sources,
  } = context;

  const bundle = await fetchTemplateData({ financialYearId: selectedFinancialYear });

  // buildSortedModules only uses sourceId to filter location-required assets,
  // which we discard below — any value is fine.
  const sortedModules = buildSortedModules({ ...bundle, sourceId: cfg_sourceIds[0] ?? 0 });

  const allowedIds = new Set(cfg_questionIds.map(Number));
  const stripped = sortedModules
    .map(([moduleName, items]) => [
      moduleName,
      items
        .filter((item) => allowedIds.has(Number(item.questionId)))
        .map(({ assets, evidences, ...rest }) => rest),
    ])
    .filter(([, items]) => items.length > 0);

  const locations = cfg_sourceIds
    .map((id) => sources.find((s) => s.id === id))
    .filter(Boolean);

  const blob = await buildTriggerWorkbook({
    sortedModules: stripped,
    locations,
    periods: cfg_selectedPeriods,
    financialYearLabel,
  });

  triggerDownload(blob, `triggers_template_${safeFile(financialYearLabel)}.xlsx`);
}

// ─── Parse ────────────────────────────────────────────────────────────────────

/**
 * Reads an uploaded workbook. All context is recovered from visible content:
 *   • sourceId  ← sheet name (unitCode)
 *   • financialYearId ← cell A1: "Financial Year: <label>"
 *   • questionId ← KPI title in column B
 *   • fromDate/toDate ← period column headers
 *
 * Emits one record per (location, KPI, period) where at least one of Min/Max
 * has a value. Empty Min+Max pairs are silently skipped.
 */
export function parseUploadedTemplate(file, { sources, questions, financialYears, units }) {
  return new Promise((resolve) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "xlsx" && ext !== "xls") {
      return resolve({ rows: [], errors: ["Only .xlsx or .xls files are accepted."] });
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(e.target.result);

        const sourceByCode = new Map(sources.map((s) => [String(s.unitCode), s]));
        const questionByTitle = new Map(
          questions.map((q) => [String(q.title || "").trim(), q])
        );
        const fyByLabel = new Map(financialYears.map((fy) => [String(fy.label), fy]));
        const unitIdByName = new Map(
          (units ?? []).map((u) => [String(u.unit || "").trim(), u.id])
        );
        const startingMonth = getStartingMonth();

        const errors = [];
        const allRows = [];

        wb.eachSheet((sheet) => {
          const sheetName = sheet.name;
          const source = sourceByCode.get(sheetName);
          if (!source) {
            errors.push(`Sheet "${sheetName}": doesn't match any known location.`);
            return;
          }

          const lastRow = sheet.actualRowCount;
          const scanLimit = Math.min(20, lastRow);

          // Locate the FY label by scanning the first few rows
          const findFyLabel = () => {
            for (let r = 1; r <= scanLimit; r++) {
              let found = null;
              sheet.getRow(r).eachCell({ includeEmpty: false }, (cell) => {
                if (found) return;
                const m = cellText(cell).trim().match(FY_LABEL_RE);
                if (m) found = m[1].trim();
              });
              if (found) return found;
            }
            return null;
          };
          const fyLabel = findFyLabel();
          if (!fyLabel) {
            errors.push(`Sheet "${sheetName}": missing "Financial Year: ..." cell.`);
            return;
          }
          const fy = fyByLabel.get(fyLabel);
          if (!fy) {
            errors.push(`Sheet "${sheetName}": unknown Financial Year "${fyLabel}".`);
            return;
          }

          // Recompute the FY's period slots so period.label → {fromDate, toDate}
          const slotsByType = buildPeriodSlots(fyLabel, startingMonth);
          const labelToSlot = new Map();
          for (const slots of Object.values(slotsByType)) {
            for (const slot of slots) labelToSlot.set(slot.label, slot);
          }

          // Locate the header row by finding one with KPI + Unit text cells
          let headerRowIdx = null;
          let kpiCol = null;
          let unitCol = null;
          for (let r = 1; r <= scanLimit; r++) {
            let foundKpi = null;
            let foundUnit = null;
            sheet.getRow(r).eachCell({ includeEmpty: false }, (cell, colNumber) => {
              const text = cellText(cell).trim();
              if (text === "KPI") foundKpi = colNumber;
              else if (text === "Unit") foundUnit = colNumber;
            });
            if (foundKpi && foundUnit) {
              headerRowIdx = r;
              kpiCol = foundKpi;
              unitCol = foundUnit;
              break;
            }
          }
          if (!headerRowIdx) {
            errors.push(`Sheet "${sheetName}": header row not found (looking for "KPI" + "Unit").`);
            return;
          }

          // Two-row header: period labels on `headerRowIdx`, "Min"/"Max" on the row below.
          // Pair adjacent Min/Max columns; read the period label from the Min column on the row above
          // (the period label is merged across Min+Max, so its master cell sits in the Min column).
          const subHeaderRowIdx = headerRowIdx + 1;
          const dataStartRow = headerRowIdx + 2;
          const minMaxCols = [];
          sheet.getRow(subHeaderRowIdx).eachCell({ includeEmpty: false }, (cell, colNumber) => {
            const text = cellText(cell).trim();
            if (text === "Min" || text === "Max") minMaxCols.push({ col: colNumber, kind: text });
          });
          minMaxCols.sort((a, b) => a.col - b.col);

          const pairs = new Map();
          for (let i = 0; i + 1 < minMaxCols.length; i += 2) {
            const minEntry = minMaxCols[i];
            const maxEntry = minMaxCols[i + 1];
            if (minEntry.kind !== "Min" || maxEntry.kind !== "Max") {
              errors.push(`Sheet "${sheetName}": malformed Min/Max header at col ${minEntry.col}.`);
              continue;
            }
            const label = cellText(sheet.getRow(headerRowIdx).getCell(minEntry.col)).trim();
            const slot = labelToSlot.get(label);
            if (!slot) {
              errors.push(`Sheet "${sheetName}": unknown period "${label}".`);
              continue;
            }
            const key = `${slot.fromDate}|${slot.toDate}`;
            pairs.set(key, {
              fromDate: slot.fromDate,
              toDate: slot.toDate,
              minCol: minEntry.col,
              maxCol: maxEntry.col,
            });
          }

          for (let r = dataStartRow; r <= lastRow; r++) {
            const row = sheet.getRow(r);
            const title = cellText(row.getCell(kpiCol)).trim();
            if (!title) continue; // skip spacer rows between modules
            const question = questionByTitle.get(title);
            if (!question) {
              errors.push(`Sheet "${sheetName}" row ${r}: unknown KPI "${title}".`);
              continue;
            }
            const unit = cellText(row.getCell(unitCol)).trim();
            const unitId = unitIdByName.get(unit) ?? null;

            for (const pair of pairs.values()) {
              if (!pair.minCol || !pair.maxCol) continue;
              const min = cellNumber(row.getCell(pair.minCol));
              const max = cellNumber(row.getCell(pair.maxCol));
              if (min === "" && max === "") continue;

              allRows.push({
                financialYearId: Number(fy.value),
                sourceId: Number(source.id),
                questionId: Number(question.questionId),
                questionTitle: title,
                unitId,
                fromDate: pair.fromDate,
                toDate: pair.toDate,
                minTrigger: min,
                maxTrigger: max,
              });
            }
          }
        });

        resolve({ rows: allRows, errors });
      } catch (err) {
        resolve({ rows: [], errors: ["Failed to read the file: " + err.message] });
      }
    };

    reader.readAsArrayBuffer(file);
  });
}

// ─── Validate ─────────────────────────────────────────────────────────────────

export function validateParsedData(rows) {
  const errors = [];
  if (!rows || rows.length === 0) {
    errors.push("No trigger values found in the file. Fill in at least one Min or Max cell.");
    return errors;
  }

  rows.forEach((r) => {
    const min = r.minTrigger;
    const max = r.maxTrigger;
    const minNum = Number(min);
    const maxNum = Number(max);
    const tag = `${r.questionTitle} @ ${r.fromDate}`;

    if (min !== "" && Number.isNaN(minNum)) {
      errors.push(`${tag}: Min must be a number.`);
    }
    if (max !== "" && Number.isNaN(maxNum)) {
      errors.push(`${tag}: Max must be a number.`);
    }
    if (
      min !== "" && max !== "" &&
      !Number.isNaN(minNum) && !Number.isNaN(maxNum) &&
      minNum >= maxNum
    ) {
      errors.push(`${tag}: Min must be less than Max.`);
    }
  });

  return errors;
}

// ─── Build Payloads ───────────────────────────────────────────────────────────

export function buildPayloads(rows) {
  return rows.map((r) => ({
    financialYearId: r.financialYearId,
    questionId: r.questionId,
    questionTitle: r.questionTitle,
    unitId: r.unitId,
    sourceId: r.sourceId,
    fromDate: r.fromDate,
    toDate: r.toDate,
    minTrigger: r.minTrigger,
    maxTrigger: r.maxTrigger,
    questionType: "quantitative",
    columnId: null,
    rowId: null,
  }));
}
